import type { PlayerProfile, TeamProfile } from '../core/contracts';

/** Detailed tactical role of a formation slot (sim-internal). */
export type SlotRole = 'GK' | 'CB' | 'FB' | 'WB' | 'DM' | 'CM' | 'WM' | 'AM' | 'W' | 'ST';

/**
 * A formation slot. `x`/`z` are metres relative to the team's block centre in the
 * team's *attacking frame* (x points at the opponent goal, z across the pitch).
 * The GK slot's x/z are ignored (keepers are positioned separately).
 */
export interface SlotDef {
  role: SlotRole;
  pos: PlayerProfile['position'];
  label: string;
  x: number;
  z: number;
  /** Typical shirt number for this slot. */
  num: number;
}

const GK: SlotDef = { role: 'GK', pos: 'GK', label: 'GK', x: 0, z: 0, num: 1 };
const BACK4: SlotDef[] = [
  { role: 'FB', pos: 'DEF', label: 'LB', x: -13, z: -24, num: 3 },
  { role: 'CB', pos: 'DEF', label: 'LCB', x: -15.5, z: -8, num: 5 },
  { role: 'CB', pos: 'DEF', label: 'RCB', x: -15.5, z: 8, num: 4 },
  { role: 'FB', pos: 'DEF', label: 'RB', x: -13, z: 24, num: 2 },
];

export const FORMATIONS: Record<TeamProfile['formation'], SlotDef[]> = {
  '4-4-2': [
    GK, ...BACK4,
    { role: 'WM', pos: 'MID', label: 'LM', x: -1, z: -23, num: 11 },
    { role: 'CM', pos: 'MID', label: 'LCM', x: -1, z: -7.5, num: 8 },
    { role: 'CM', pos: 'MID', label: 'RCM', x: -1, z: 7.5, num: 6 },
    { role: 'WM', pos: 'MID', label: 'RM', x: -1, z: 23, num: 7 },
    { role: 'ST', pos: 'FWD', label: 'LS', x: 12, z: -6, num: 10 },
    { role: 'ST', pos: 'FWD', label: 'RS', x: 12, z: 6, num: 9 },
  ],
  '4-3-3': [
    GK, ...BACK4,
    { role: 'CM', pos: 'MID', label: 'LCM', x: 0, z: -11, num: 8 },
    { role: 'DM', pos: 'MID', label: 'DM', x: -6, z: 0, num: 6 },
    { role: 'CM', pos: 'MID', label: 'RCM', x: 0, z: 11, num: 10 },
    { role: 'W', pos: 'FWD', label: 'LW', x: 12, z: -23, num: 11 },
    { role: 'ST', pos: 'FWD', label: 'ST', x: 14, z: 0, num: 9 },
    { role: 'W', pos: 'FWD', label: 'RW', x: 12, z: 23, num: 7 },
  ],
  '4-2-3-1': [
    GK, ...BACK4,
    { role: 'DM', pos: 'MID', label: 'LDM', x: -5.5, z: -7, num: 6 },
    { role: 'DM', pos: 'MID', label: 'RDM', x: -5.5, z: 7, num: 8 },
    { role: 'W', pos: 'MID', label: 'LAM', x: 6, z: -22, num: 11 },
    { role: 'AM', pos: 'MID', label: 'CAM', x: 6.5, z: 0, num: 10 },
    { role: 'W', pos: 'MID', label: 'RAM', x: 6, z: 22, num: 7 },
    { role: 'ST', pos: 'FWD', label: 'ST', x: 15, z: 0, num: 9 },
  ],
  '3-5-2': [
    GK,
    { role: 'CB', pos: 'DEF', label: 'LCB', x: -15, z: -13, num: 5 },
    { role: 'CB', pos: 'DEF', label: 'CB', x: -16.5, z: 0, num: 4 },
    { role: 'CB', pos: 'DEF', label: 'RCB', x: -15, z: 13, num: 6 },
    { role: 'WB', pos: 'DEF', label: 'LWB', x: -4, z: -27, num: 3 },
    { role: 'CM', pos: 'MID', label: 'LCM', x: 0, z: -10, num: 8 },
    { role: 'DM', pos: 'MID', label: 'DM', x: -6, z: 0, num: 14 },
    { role: 'CM', pos: 'MID', label: 'RCM', x: 0, z: 10, num: 7 },
    { role: 'WB', pos: 'DEF', label: 'RWB', x: -4, z: 27, num: 2 },
    { role: 'ST', pos: 'FWD', label: 'LS', x: 13, z: -6, num: 10 },
    { role: 'ST', pos: 'FWD', label: 'RS', x: 13, z: 6, num: 9 },
  ],
};
